/**
 * NotificationItem Component - Epic 47
 *
 * Individual notification item in the dropdown list.
 * Shows summary with expandable details.
 */
import React from 'react';
import { ChevronDown, ChevronUp, Bell, AlertTriangle } from 'lucide-react';
import { Notificacao } from '../../services/notificationService';

interface NotificationItemProps {
  notificacao: Notificacao;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onToggleLeitura: (id: number) => void;
  onViewJob: (jobId: number) => void;
}

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notificacao,
  isExpanded,
  onToggleExpand,
  onToggleLeitura,
  onViewJob,
}) => {
  // Epic 47: Safe access to conteudo with fallback
  const conteudo = notificacao.conteudo || {};
  const isViolacaoAbaixo = conteudo.tipo_violacao === 'ABAIXO_MINIMO';

  return (
    <div
      className={`border-b border-gray-200 dark:border-gray-700 last:border-b-0 transition-colors ${
        !notificacao.lida ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-white dark:bg-gray-800'
      }`}
    >
      {/* Summary Row */}
      <button
        onClick={onToggleExpand}
        className="w-full p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <div className="flex items-start gap-3">
          {/* Indicator */}
          <div className="mt-1 flex-shrink-0">
            {!notificacao.lida ? (
              <div className="w-2.5 h-2.5 rounded-full bg-red-500" title="Não lida" />
            ) : (
              <div className="w-2.5 h-2.5 rounded-full bg-gray-300 dark:bg-gray-600" title="Lida" />
            )}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Title */}
            <div className="flex items-center gap-2">
              <AlertTriangle
                className={`w-4 h-4 flex-shrink-0 ${
                  isViolacaoAbaixo ? 'text-orange-500' : 'text-yellow-500'
                }`}
              />
              <span className={`text-sm font-medium truncate ${
                !notificacao.lida
                  ? 'text-gray-900 dark:text-white'
                  : 'text-gray-700 dark:text-gray-300'
              }`}>
                {notificacao.titulo}
              </span>
            </div>

            {/* Subtitle */}
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {notificacao.descricao_curta}
            </p>

            {/* Preview */}
            {notificacao.preview_observacao && (
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1 line-clamp-1 italic">
                "{notificacao.preview_observacao}"
              </p>
            )}
          </div>

          {/* Expand Icon */}
          <div className="flex-shrink-0 text-gray-400">
            {isExpanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </div>
        </div>
      </button>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-4 pb-3 pt-1 bg-gray-50 dark:bg-gray-900/50">
          <div className="space-y-2 text-sm">
            {/* Hospital */}
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Hospital:</span>
              <span className="text-gray-900 dark:text-white font-medium text-right max-w-[60%] truncate">
                {conteudo.hospital_nome}
              </span>
            </div>

            {/* Intervalo */}
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Intervalo:</span>
              <span className={`font-medium ${
                isViolacaoAbaixo ? 'text-orange-600 dark:text-orange-400' : 'text-yellow-600 dark:text-yellow-400'
              }`}>
                {conteudo.intervalo_atual} dias ({isViolacaoAbaixo ? 'mín' : 'máx'}: {conteudo.intervalo_configurado} dias)
              </span>
            </div>

            {/* Aprovado por */}
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Aprovado por:</span>
              <span className="text-gray-900 dark:text-white">
                {conteudo.aprovado_por_nome}
              </span>
            </div>

            {/* Observação completa */}
            {conteudo.observacao_completa && (
              <div className="mt-2 p-2 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Observação:</p>
                <p className="text-sm text-gray-700 dark:text-gray-300 italic">
                  "{conteudo.observacao_completa}"
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2 mt-3 pt-2 border-t border-gray-200 dark:border-gray-700">
              {notificacao.job_id && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onViewJob(notificacao.job_id!);
                  }}
                  className="flex-1 px-3 py-1.5 text-xs font-medium bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                >
                  Ver Contagem #{notificacao.job_id}
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleLeitura(notificacao.id);
                }}
                className="flex-1 px-3 py-1.5 text-xs font-medium bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded transition-colors"
              >
                {notificacao.lida ? 'Marcar como não lida' : 'Marcar como lida'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationItem;
